/**
 * BudgetAssignmentCard - Expandable card for viewing/editing budget assignment distributions
 */

import React, { useState, useCallback, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  CurrencyEur,
  Tag,
  Cube,
  Percent,
  Ruler,
  CaretDown,
  CaretUp,
  PencilSimple,
  FloppyDisk,
  X,
  Plus,
  Spinner,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UnitDTO } from "shared/types/unit";
import { formatCurrency, UnitDistribution } from "./types";

interface BudgetAssignmentCardProps {
  assignment: any;
  buildingUnits: UnitDTO[];
  onUpdate: () => void;
}

export const BudgetAssignmentCard: React.FC<BudgetAssignmentCardProps> = ({
  assignment,
  buildingUnits,
  onUpdate,
}) => {
  const { session } = useSession();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editDistribution, setEditDistribution] = useState<UnitDistribution>({});
  const [editUnitIds, setEditUnitIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const distributions = assignment.unitDistributions || [];
  const hasDistributions = distributions.length > 0;
  const assignedAmount = Number(assignment.assignedAmount || 0);

  // Get unit details
  const getUnitDetails = useCallback(
    (unitId: string) => buildingUnits.find((u) => u.id === unitId),
    [buildingUnits]
  );

  // Start editing
  const startEdit = useCallback(() => {
    const existingUnitIds = distributions.map((d: any) => d.unit?.id).filter(Boolean);
    const distribution: UnitDistribution = {};
    distributions.forEach((d: any) => {
      if (d.unit?.id) {
        distribution[d.unit.id] = Number(d.percentage || 0);
      }
    });
    setEditUnitIds(existingUnitIds);
    setEditDistribution(distribution);
    setIsEditing(true);
    setIsExpanded(true);
  }, [distributions]);

  // Cancel editing
  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditDistribution({});
    setEditUnitIds([]);
  }, []);

  // Handle distribution change
  const handleDistributionChange = useCallback((unitId: string, value: number) => {
    setEditDistribution((prev) => ({
      ...prev,
      [unitId]: Math.max(0, Math.min(100, value)),
    }));
  }, []);

  // Add unit
  const addUnit = useCallback((unitId: string) => {
    if (editUnitIds.includes(unitId)) return;
    const newUnitIds = [...editUnitIds, unitId];
    setEditUnitIds(newUnitIds);

    // Recalculate equal distribution
    const equalPct = 100 / newUnitIds.length;
    const newDistribution: UnitDistribution = {};
    newUnitIds.forEach((id) => {
      newDistribution[id] = Math.round(equalPct * 100) / 100;
    });
    setEditDistribution(newDistribution);
  }, [editUnitIds]);

  // Remove unit
  const removeUnit = useCallback((unitId: string) => {
    const newUnitIds = editUnitIds.filter((id) => id !== unitId);
    setEditUnitIds(newUnitIds);

    if (newUnitIds.length === 0) {
      setEditDistribution({});
    } else {
      const equalPct = 100 / newUnitIds.length;
      const newDistribution: UnitDistribution = {};
      newUnitIds.forEach((id) => {
        newDistribution[id] = Math.round(equalPct * 100) / 100;
      });
      setEditDistribution(newDistribution);
    }
  }, [editUnitIds]);

  // Calculate total percentage
  const totalPercentage = useMemo(() => {
    return Object.values(editDistribution).reduce((sum, pct) => sum + (pct || 0), 0);
  }, [editDistribution]);

  // Check if valid
  const isValid = useMemo(() => {
    if (editUnitIds.length === 0) return false;
    return Math.abs(totalPercentage - 100) < 0.01;
  }, [editUnitIds.length, totalPercentage]);

  // Quick actions
  const setEqualDistribution = useCallback(() => {
    if (editUnitIds.length === 0) return;
    const equalPct = 100 / editUnitIds.length;
    const distribution: UnitDistribution = {};
    editUnitIds.forEach((id) => {
      distribution[id] = Math.round(equalPct * 100) / 100;
    });
    setEditDistribution(distribution);
  }, [editUnitIds]);

  const setSurfaceDistribution = useCallback(() => {
    const totalSurface = editUnitIds.reduce((sum, id) => {
      const unit = getUnitDetails(id);
      return sum + (unit?.netSurface || unit?.grossSurface || 0);
    }, 0);

    if (totalSurface === 0) {
      toast.error("Geen oppervlakte data beschikbaar");
      return;
    }

    const distribution: UnitDistribution = {};
    editUnitIds.forEach((id) => {
      const unit = getUnitDetails(id);
      const surface = unit?.netSurface || unit?.grossSurface || 0;
      distribution[id] = Math.round((surface / totalSurface) * 10000) / 100;
    });
    setEditDistribution(distribution);
  }, [editUnitIds, getUnitDetails]);

  // Save
  const save = useCallback(async () => {
    if (!session || !isValid) return;

    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/part/assignment/${assignment.id}/distribution`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            unitIds: editUnitIds,
            unitDistribution: editDistribution,
          }),
        },
        session
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to update distribution");
      }

      toast.success("Verdeling succesvol opgeslagen");
      cancelEdit();
      onUpdate();
    } catch (error) {
      console.error("Error saving distribution:", error);
      toast.error(
        error instanceof Error ? error.message : "Fout bij opslaan verdeling"
      );
    } finally {
      setIsSaving(false);
    }
  }, [session, isValid, assignment.id, editUnitIds, editDistribution, cancelEdit, onUpdate]);

  return (
    <div className="rounded-md border border-neutral-200 bg-white overflow-hidden">
      {/* Header - clickable */}
      <div
        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-neutral-50 transition-colors ${isExpanded ? "bg-neutral-50" : ""}`}
        onClick={() => !isEditing && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <CurrencyEur className="h-5 w-5 text-moss-primary" weight="fill" />
          <div>
            <p className="text-sm font-medium text-neutral-700">
              {assignment.investmentItem?.title || "Investeringslijn"}
            </p>
            {assignment.investmentItem?.label && (
              <Badge variant="outline" className="mt-1 border-brick-primary/30 text-brick-primary">
                <Tag className="mr-1 h-3 w-3" />
                {assignment.investmentItem.label.name}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm font-semibold text-moss-primary">
              {formatCurrency(assignedAmount)}
            </p>
            {assignment.notes && (
              <p className="mt-1 max-w-[200px] truncate text-xs text-neutral-500">
                {assignment.notes}
              </p>
            )}
          </div>
          {isExpanded ? (
            <CaretUp className="h-4 w-4 text-neutral-400" />
          ) : (
            <CaretDown className="h-4 w-4 text-neutral-400" />
          )}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-neutral-200 bg-neutral-50 px-3 py-3">
          {/* Header with edit button */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-neutral-600 flex items-center gap-1">
              <Percent className="h-3 w-3" />
              Verdeling per kostenplaats
            </p>
            {!isEditing && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  startEdit();
                }}
                className="h-7 text-xs text-moss-primary hover:text-moss-primary hover:bg-moss-primary/10"
              >
                <PencilSimple className="mr-1 h-3 w-3" />
                {hasDistributions ? "Bewerken" : "Kostenplaatsen toevoegen"}
              </Button>
            )}
          </div>

          {/* View mode */}
          {!isEditing && hasDistributions && (
            <div className="space-y-2">
              {distributions.map((dist: any) => {
                const surface = dist.unit?.netSurface || dist.unit?.grossSurface || 0;
                const amount = Number(dist.amount || 0);
                const eurPerM2 = surface > 0 ? amount / surface : 0;

                return (
                  <div
                    key={dist.id}
                    className="flex items-center justify-between rounded-md border border-neutral-200 bg-white p-2 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <Cube className="h-3 w-3 text-moss-primary" />
                      <span className="font-medium text-neutral-700">{dist.unit?.id}</span>
                      {surface > 0 && (
                        <span className="text-neutral-400">({surface.toFixed(0)} m²)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-500">{Number(dist.percentage || 0).toFixed(1)}%</span>
                      <span className="font-medium text-moss-primary">{formatCurrency(amount)}</span>
                      {surface > 0 && eurPerM2 > 0 && (
                        <span className="text-neutral-400">({formatCurrency(eurPerM2)}/m²)</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* View mode - no distributions */}
          {!isEditing && !hasDistributions && (
            <div className="rounded-md border border-dashed border-neutral-300 bg-white p-4 text-center">
              <Cube className="mx-auto h-6 w-6 text-neutral-300" />
              <p className="mt-1 text-xs text-neutral-500">Geen kostenplaatsen gekoppeld</p>
              <p className="mt-0.5 text-xs text-neutral-400">
                Klik op "Kostenplaatsen toevoegen" om te verdelen
              </p>
            </div>
          )}

          {/* Edit mode */}
          {isEditing && (
            <div className="space-y-3">
              {/* Add units */}
              {editUnitIds.length < buildingUnits.length && (
                <div className="space-y-2">
                  <Label className="text-xs text-neutral-600">
                    {editUnitIds.length === 0 ? "Selecteer kostenplaatsen:" : "Voeg kostenplaats toe:"}
                  </Label>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {buildingUnits
                      .filter((unit) => unit.id && !editUnitIds.includes(unit.id))
                      .map((unit) => (
                        <Button
                          key={unit.id}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addUnit(unit.id!)}
                          className="h-7 text-xs"
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          {unit.id}
                        </Button>
                      ))}
                  </div>
                </div>
              )}

              {/* Distribution inputs */}
              {editUnitIds.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-neutral-600">Verdeling:</Label>
                  {editUnitIds.map((unitId) => {
                    const unit = getUnitDetails(unitId);
                    const percentage = editDistribution[unitId] || 0;
                    const calculatedAmount = (assignedAmount * percentage) / 100;
                    const surface = unit?.netSurface || unit?.grossSurface || 0;
                    const eurPerM2 = surface > 0 ? calculatedAmount / surface : 0;

                    return (
                      <div key={unitId} className="rounded-md border border-neutral-200 bg-white p-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Cube className="h-3 w-3 flex-shrink-0 text-moss-primary" />
                            <span className="font-medium text-xs text-neutral-700 truncate">{unitId}</span>
                            {surface > 0 && (
                              <span className="text-xs text-neutral-400 flex-shrink-0">
                                ({surface.toFixed(0)} m²)
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="relative w-16">
                              <Input
                                type="number"
                                value={percentage || ""}
                                onChange={(e) => {
                                  const value = e.target.value === "" ? 0 : parseFloat(e.target.value);
                                  handleDistributionChange(unitId, isNaN(value) ? 0 : value);
                                }}
                                className="h-7 pr-5 text-right text-xs"
                                min={0}
                                max={100}
                                step={0.5}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400">%</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeUnit(unitId)}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-1 flex items-center justify-between text-xs border-t border-neutral-100 pt-1">
                          <span className="text-moss-primary font-medium">{formatCurrency(calculatedAmount)}</span>
                          {surface > 0 && calculatedAmount > 0 && (
                            <span className="text-neutral-400">{formatCurrency(eurPerM2)}/m²</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Total percentage */}
              {editUnitIds.length > 0 && (
                <div
                  className={`flex items-center justify-between rounded-md p-2 text-xs ${
                    isValid ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"
                  }`}
                >
                  <span className="font-medium">Totaal:</span>
                  <span className={`font-bold ${isValid ? "text-green-700" : "text-amber-700"}`}>
                    {totalPercentage.toFixed(1)}%
                    {!isValid && <span className="ml-1 font-normal">(moet 100%)</span>}
                  </span>
                </div>
              )}

              {/* Quick actions */}
              {editUnitIds.length > 1 && (
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={setEqualDistribution} className="h-7 text-xs">
                    Gelijke verdeling
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={setSurfaceDistribution} className="h-7 text-xs">
                    <Ruler className="mr-1 h-3 w-3" />
                    Op m²
                  </Button>
                </div>
              )}

              {/* Save/Cancel */}
              <div className="flex justify-end gap-2 border-t border-neutral-200 pt-3">
                <Button type="button" variant="outline" size="sm" onClick={cancelEdit} disabled={isSaving} className="h-8">
                  Annuleren
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={save}
                  disabled={isSaving || !isValid}
                  className="h-8 bg-moss-primary hover:bg-moss-primary/90"
                >
                  {isSaving ? (
                    <>
                      <Spinner className="mr-1 h-3 w-3 animate-spin" />
                      Opslaan...
                    </>
                  ) : (
                    <>
                      <FloppyDisk className="mr-1 h-3 w-3" />
                      Opslaan
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BudgetAssignmentCard;
